import { LogLevel, Purchases, type CustomerInfo, type Offerings, type Package } from "@revenuecat/purchases-js";
import type { User } from "../types/user";

export type RevenueCatWebConfig = {
  apiKey: string;
  entitlementId: string;
  yearlyProductId: string;
  discountedYearlyProductId: string;
};

export const getRevenueCatWebConfig = (): RevenueCatWebConfig => ({
  apiKey: process.env.REACT_APP_REVENUECAT_API_KEY || "",
  entitlementId: "Arpagon Pro",
  yearlyProductId:  "yearly",
  discountedYearlyProductId:  "yearly_discounted",
});

let purchasesInstance: Purchases | null = null;
let configuredForUserId: string | null = null;

export const ensureRevenueCatConfigured = async (user: User): Promise<Purchases> => {
  const { apiKey } = getRevenueCatWebConfig();
  if (!apiKey) {
    throw new Error("Missing REACT_APP_REVENUECAT_API_KEY");
  }

  const appUserId = String(user.id || user.email);

  if (!Purchases.isConfigured()) {
    if (process.env.NODE_ENV !== "production") {
      Purchases.setLogLevel(LogLevel.Debug);
    }
    purchasesInstance = Purchases.configure({ apiKey, appUserId });
    configuredForUserId = appUserId;
    return purchasesInstance;
  }

  purchasesInstance = purchasesInstance ?? Purchases.getSharedInstance();
  if (configuredForUserId !== appUserId && purchasesInstance.getAppUserId() !== appUserId) {
    await purchasesInstance.changeUser(appUserId);
    configuredForUserId = appUserId;
  }

  return purchasesInstance;
};

export const getRevenueCatCustomerInfo = async (user: User): Promise<CustomerInfo> => {
  const purchases = await ensureRevenueCatConfigured(user);
  return await purchases.getCustomerInfo();
};

export const isArpagonPro = async (user: User): Promise<boolean> => {
  const purchases = await ensureRevenueCatConfigured(user);
  const { entitlementId } = getRevenueCatWebConfig();
  return await purchases.isEntitledTo(entitlementId);
};

export const getOfferings = async (user: User): Promise<Offerings> => {
  const purchases = await ensureRevenueCatConfigured(user);
  return await purchases.getOfferings();
};

const findYearlyPackage = (offerings: Offerings, yearlyProductId: string): Package => {
  const current = offerings.current;
  if (current) {
    const pkg = current.availablePackages.find(
      (p) => p.webBillingProduct.identifier === yearlyProductId
    );
    if (pkg) return pkg;
  }

  for (const offeringId in offerings.all) {
    const offering = offerings.all[offeringId];
    const pkg = offering.availablePackages.find(
      (p) => p.webBillingProduct.identifier === yearlyProductId
    );
    if (pkg) return pkg;
  }

  const fallback = current?.annual ?? current?.availablePackages[0];

  if (!fallback) {
    throw new Error(`No purchasable package found for '${yearlyProductId}'.`);
  }

  if (
    fallback.webBillingProduct.identifier === yearlyProductId ||
    yearlyProductId === getRevenueCatWebConfig().yearlyProductId
  ) {
    return fallback;
  }

  throw new Error(
    `Product '${yearlyProductId}' not found in any offering. Please check your RevenueCat dashboard.`
  );
};

export const purchaseYearly = async (
  user: User,
  opts?: { htmlTarget?: HTMLElement; productId?: string }
): Promise<CustomerInfo> => {
  const purchases = await ensureRevenueCatConfigured(user);
  const { yearlyProductId } = getRevenueCatWebConfig();
  const productId = opts?.productId || yearlyProductId;

  const offerings = await purchases.getOfferings();
  const rcPackage = findYearlyPackage(offerings, productId);

  const result = await purchases.purchase({
    rcPackage,
    customerEmail: user.email,
    htmlTarget: opts?.htmlTarget,
    skipSuccessPage: true,
  });

  return result.customerInfo;
};

type PurchasesWithPaywall = Purchases & {
  presentPaywall: (args: { htmlTarget: HTMLElement; offering?: unknown }) => Promise<unknown>;
};

export const presentPaywall = async (user: User, htmlTarget: HTMLElement): Promise<unknown> => {
  const purchases = (await ensureRevenueCatConfigured(user)) as PurchasesWithPaywall;
  return await purchases.presentPaywall({ htmlTarget });
};

